import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  ShieldCheckIcon,
  CheckCircleIcon,
  InformationCircleIcon,
  QrCodeIcon,
  ArrowPathIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../../../features/theme/context/ThemeContext';
import { useAuthContext } from '../../../features/auth/context/AuthContext';
import TwoFactorSetupModal from '../../../features/profile/sections/organization/TwoFactorSetupModal';

const API_BASE = process.env.REACT_APP_API_URL || '';

const formatDateTime = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInHours = diffInMs / (1000 * 60 * 60);
  const diffInDays = diffInMs / (1000 * 60 * 60 * 24);
  const timeOptions = { hour: 'numeric', minute: '2-digit', hour12: true };
  const timeString = date.toLocaleTimeString('en-US', timeOptions);
  if (diffInHours < 24) return `Today at ${timeString}`;
  if (diffInDays < 7) return `${date.toLocaleDateString('en-US', { weekday: 'long' })} at ${timeString}`;
  return `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} at ${timeString}`;
};

function SecurityTab() {
  const { user } = useAuthContext();
  const { darkMode } = useTheme();
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organization, setOrganization] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [twoFactorSettings, setTwoFactorSettings] = useState({ enforced: false, enforcedBy: null, enforcedAt: null });
  const [savedTwoFactorSettings, setSavedTwoFactorSettings] = useState({ enforced: false, enforcedBy: null, enforcedAt: null });
  const [user2FAStatus, setUser2FAStatus] = useState({ enabled: false, setupAt: null });
  const [enforcedByUser, setEnforcedByUser] = useState(null);
  const [show2FASetupModal, setShow2FASetupModal] = useState(false);
  const [show2FADisableModal, setShow2FADisableModal] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const canManagePreferences = userRole === 'admin' || userRole === 'owner';

  const toastStyle = {
    style: {
      background: darkMode ? '#333' : '#fff',
      color: darkMode ? '#fff' : '#000',
      border: `1px solid ${darkMode ? '#444' : '#e2e8f0'}`,
    },
    success: {
      style: {
        background: darkMode ? '#0c4a2d' : '#ecfdf5',
        color: darkMode ? '#fff' : '#065f46',
        border: `1px solid ${darkMode ? '#065f46' : '#d1fae5'}`,
      },
    },
    error: {
      style: {
        background: darkMode ? '#4c1d24' : '#fef2f2',
        color: darkMode ? '#fff' : '#b91c1c',
        border: `1px solid ${darkMode ? '#b91c1c' : '#fee2e2'}`,
      },
    },
  };

  const fetchEnforcedByUser = async (userId) => {
    if (!userId) {
      setEnforcedByUser(null);
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE}/organization/user/${userId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) setEnforcedByUser(response.data.user);
    } catch {
      setEnforcedByUser(null);
    }
  };

  const fetchTwoFactorSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const orgResponse = await axios.get(`${API_BASE}/organization/2fa-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (orgResponse.data.success) {
        setTwoFactorSettings(orgResponse.data.settings);
        setSavedTwoFactorSettings(orgResponse.data.settings);
        if (orgResponse.data.settings.enforcedBy) {
          fetchEnforcedByUser(orgResponse.data.settings.enforcedBy);
        }
      }

      const userResponse = await axios.get(`${API_BASE}/auth/2fa/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (userResponse.data.success) {
        setUser2FAStatus(userResponse.data.status);
      }
    } catch (error) {
      console.error('Error fetching 2FA settings:', error);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        setIsLoading(true);
        const token = localStorage.getItem('token');
        if (!token) {
          setIsLoading(false);
          return;
        }

        const orgRes = await axios.get(`${API_BASE}/organization`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (orgRes.data.hasOrganization && orgRes.data.organization) {
          setOrganization(orgRes.data.organization);
          setUserRole(orgRes.data.organization.userRole || 'member');
        }

        await fetchTwoFactorSettings();
      } catch (error) {
        console.error('Error loading security settings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    setHasChanges(twoFactorSettings.enforced !== savedTwoFactorSettings.enforced);
  }, [twoFactorSettings.enforced, savedTwoFactorSettings.enforced]);

  const handle2FAToggle = (enabled) => {
    if (!canManagePreferences || !organization) return;
    if (enabled && !user2FAStatus.enabled) {
      setShow2FASetupModal(true);
      return;
    }
    if (!enabled && savedTwoFactorSettings.enforced) {
      setShow2FADisableModal(true);
      return;
    }
    setTwoFactorSettings((prev) => ({ ...prev, enforced: enabled }));
  };

  const handleSavePreferences = async () => {
    if (!hasChanges || !canManagePreferences) return;
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API_BASE}/organization/2fa-enforcement`,
        { enforced: twoFactorSettings.enforced },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setTwoFactorSettings(response.data.settings);
        setSavedTwoFactorSettings(response.data.settings);
        if (response.data.settings.enforcedBy) {
          fetchEnforcedByUser(response.data.settings.enforcedBy);
        }
        setHasChanges(false);
      }
    } catch (error) {
      console.error('Error saving 2FA settings:', error);
    } finally {
      setSaving(false);
    }
  };

  const confirmDisable2FA = () => {
    setTwoFactorSettings((prev) => ({ ...prev, enforced: false }));
    setShow2FADisableModal(false);
  };

  if (!user) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex flex-col items-center justify-center text-center">
        <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
          Sign in to view security settings
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4 flex items-center justify-center">
        <p className="text-gray-500 dark:text-[#999999]" style={{ fontFamily: 'var(--chat-font-family)' }}>
          Loading...
        </p>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4" style={{ fontFamily: 'var(--chat-font-family)' }}>
      <div className="space-y-0">
        {/* Two-Factor Authentication */}
        <section className="pb-5 border-b border-gray-100 dark:border-[#2a2a2a]">
          <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-3">Two-factor authentication</h3>

          {/* User's 2FA status - always shown */}
          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-2">
              <QrCodeIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">Authenticator app</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {user2FAStatus.enabled ? 'Your account is protected with 2FA' : 'Add an extra layer of security'}
                </p>
              </div>
            </div>
            {user2FAStatus.enabled ? (
              <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                <CheckCircleIcon className="w-4 h-4" />
                Setup complete
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setShow2FASetupModal(true)}
                className="account-modal-btn px-4 py-2 rounded-lg border border-gray-200 dark:border-[#2a2a2a] bg-gray-50/50 dark:bg-[#181818] text-gray-900 dark:text-white text-sm font-medium hover:bg-gray-100 dark:hover:bg-[#252525]"
              >
                Set up 2FA
              </button>
            )}
          </div>

          {/* Org 2FA enforcement - only for admins in org */}
          {organization && canManagePreferences && (
            <div className="pt-4 mt-4 border-t border-gray-100 dark:border-[#2a2a2a]">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheckIcon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Require 2FA for organization</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      Enforce 2FA for all organization members
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => handle2FAToggle(!twoFactorSettings.enforced)}
                  disabled={saving}
                  className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors flex-shrink-0 ${
                    twoFactorSettings.enforced
                      ? 'bg-black border-black dark:bg-white dark:border-white'
                      : 'bg-transparent border-gray-400 dark:border-gray-500 hover:border-gray-500 dark:hover:border-gray-400'
                  } ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {twoFactorSettings.enforced && (
                    <CheckIcon className={`w-3 h-3 stroke-[4px] ${twoFactorSettings.enforced ? 'text-white dark:text-black' : ''}`} />
                  )}
                </button>
              </div>

              {savedTwoFactorSettings.enforcedAt && (
                <div className="mt-4 p-3 bg-gray-50 dark:bg-[#1a1a1a] rounded-lg border border-gray-200 dark:border-[#282828]">
                  <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                    <InformationCircleIcon className="w-3 h-3 mr-2 flex-shrink-0" />
                    <span>
                      Two-factor authentication was{' '}
                      <span className={savedTwoFactorSettings.enforced ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}>
                        {savedTwoFactorSettings.enforced ? 'enabled' : 'disabled'}
                      </span>
                      {formatDateTime(savedTwoFactorSettings.enforcedAt) && (
                        <> {formatDateTime(savedTwoFactorSettings.enforcedAt)}</>
                      )}
                      {enforcedByUser && (
                        <> by {enforcedByUser.firstName} {enforcedByUser.lastName}</>
                      )}
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}

          {hasChanges && canManagePreferences && organization && (
            <div className="flex justify-end mt-4">
              <button
                onClick={handleSavePreferences}
                disabled={saving}
                className="account-modal-btn px-4 py-2 bg-black dark:bg-white text-white dark:text-black rounded-lg hover:bg-gray-800 dark:hover:bg-gray-200 disabled:opacity-50 text-sm font-medium flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <ArrowPathIcon className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save changes'
                )}
              </button>
            </div>
          )}
        </section>

        {!organization && (
          <p className="text-xs text-gray-500 dark:text-gray-400 py-2">
            Join an organization to manage organization-wide 2FA enforcement.
          </p>
        )}
      </div>

      <TwoFactorSetupModal
        isOpen={show2FASetupModal}
        onClose={() => setShow2FASetupModal(false)}
        onSetupComplete={() => {
          fetchTwoFactorSettings();
        }}
        toastStyle={toastStyle}
        darkMode={darkMode}
      />

      {show2FADisableModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10001] flex items-center justify-center p-4">
          <div
            className="bg-white dark:bg-[#181818] rounded-2xl shadow-xl max-w-md w-full p-6 border border-gray-200 dark:border-[#282828]"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center space-x-3">
                <div className="bg-red-100 dark:bg-red-900/30 p-2 rounded-lg">
                  <ShieldCheckIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Disable 2FA enforcement</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">This will affect organization security</p>
                </div>
              </div>
            </div>
            <div className="space-y-4 mb-6">
              <p className="text-gray-700 dark:text-gray-300 text-sm leading-6">
                Are you sure you want to disable two-factor authentication enforcement for your organization?
              </p>
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-xl border border-amber-200 dark:border-amber-800">
                <p className="text-amber-800 dark:text-amber-200 text-sm font-medium mb-2">Security impact:</p>
                <ul className="text-amber-700 dark:text-amber-300 text-sm space-y-1">
                  <li>• Members with 2FA will no longer be required to use it</li>
                  <li>• Organization security posture will be reduced</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShow2FADisableModal(false)}
                className="account-modal-btn flex-1 px-4 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                onClick={confirmDisable2FA}
                className="account-modal-btn flex-1 bg-red-600 text-white px-4 py-2.5 rounded-lg hover:bg-red-700 font-medium"
              >
                Disable 2FA
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SecurityTab;
